import { useState } from "react";
import { FileText, Loader2, Search } from "lucide-react";
import { AttachmentChips } from "@/components/files/AttachmentChips";
import { DeleteFileDialog, RenameFileDialog } from "@/components/files/FileDialogs";
import { FileDetailSheet } from "@/components/files/FileDetailSheet";
import { FileRow } from "@/components/files/FileRow";
import { UploadDropzone } from "@/components/files/UploadDropzone";
import { useFiles } from "@/hooks/queries";
import { useDebounce } from "@/hooks/useDebounce";
import { useUploads } from "@/hooks/useUploads";
import { FILTERS, formatBytes } from "@/lib/files";
import { cn } from "@/lib/utils";

export default function FilesPage() {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [openId, setOpenId] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const query = useDebounce(q.trim(), 300);
  const { data, isLoading, isFetching, isError, error, refetch } = useFiles({ q: query, kind });
  const uploads = useUploads();
  const inFlight = uploads.items.filter((it) => it.status === "uploading" || it.status === "error");
  const items = data?.items || [];
  const storage = data?.storage;

  return (
    <div className="h-full overflow-y-auto" data-testid="files-page">
      <div className="mx-auto w-full max-w-[860px] px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <h1 className="text-[1.7rem] font-semibold tracking-tight text-white sm:text-3xl">Files</h1>
        <p className="mt-1 text-sm text-zinc-500">Private to your account. Attach documents to a chat and ChatSleek will read them.</p>
        <div className="mt-8">
          <UploadDropzone onFiles={uploads.addFiles} />
          {inFlight.length > 0 && <AttachmentChips items={inFlight} onRemove={uploads.remove} className="mt-3" />}
        </div>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex h-11 flex-1 items-center gap-3 rounded-xl border border-white/[0.09] bg-ink-850 px-4 focus-within:border-white/20">
            <Search className="h-4 w-4 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…" maxLength={200} data-testid="files-search-input" className="h-full flex-1 bg-transparent text-sm text-white placeholder:text-zinc-500 focus:outline-none" />
            {isFetching && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
          </div>
          {storage && (
            <span className="shrink-0 text-xs text-zinc-500" data-testid="files-storage-usage">
              {storage.files} files · {formatBytes(storage.used_bytes)} of {formatBytes(storage.quota_bytes)}
            </span>
          )}
        </div>
        <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1" data-testid="files-filters">
          {FILTERS.map((f) => (
            <button key={f.key || "all"} type="button" onClick={() => setKind(f.key)} data-testid={`files-filter-${f.key || "all"}`} className={cn("shrink-0 rounded-full border px-3 py-1 text-xs transition-colors", kind === f.key ? "border-white bg-white text-ink-950" : "border-white/10 text-zinc-400 hover:text-white")}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-0.5" data-testid="files-list">
          {isLoading && [0, 1, 2].map((i) => <div key={i} className="mx-3 my-5 h-4 animate-pulse rounded bg-white/[0.05]" style={{ width: `${75 - i * 12}%` }} />)}
          {isError && (
            <div className="rounded-xl border border-white/10 p-4 text-sm text-zinc-300" data-testid="files-error">
              {error.message} <button type="button" className="underline underline-offset-4" onClick={() => refetch()}>Try again</button>
            </div>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <div className="flex flex-col items-center py-16 text-center" data-testid={query || kind ? "files-no-results" : "files-empty-state"}>
              <FileText className="h-8 w-8 text-zinc-700" strokeWidth={1.4} />
              <p className="mt-4 text-sm text-zinc-300">{query || kind ? "No files match." : "No files yet."}</p>
              {!query && !kind && <p className="mt-1 text-xs text-zinc-500">Upload a document to ask ChatSleek about it.</p>}
            </div>
          )}
          {items.map((f) => (
            <FileRow key={f.id} file={f} onOpen={(x) => setOpenId(x.id)} onRename={setRenaming} onDelete={setDeleting} />
          ))}
        </div>
      </div>
      <FileDetailSheet fileId={openId} onClose={() => setOpenId(null)} />
      {renaming && <RenameFileDialog file={renaming} onClose={() => setRenaming(null)} />}
      {deleting && <DeleteFileDialog file={deleting} onClose={() => setDeleting(null)} onDeleted={() => openId === deleting.id && setOpenId(null)} />}
    </div>
  );
}
